import type { CommercialTemplateId, HomeStageId } from "@townsy/contracts";

/**
 * Parametric building shells. These describe original procedural geometry (no third-party
 * assets): the client builds meshes from these numbers, the manifest derives anchors and
 * colliders from them.
 *
 * Founder feedback 2026-10-09: every commercial building starts at the SAME height and grows into a
 * skyscraper as it is bought again and again. So a commercial building = facade STYLE (below, fixed
 * per plot) + HEIGHT STAGE (from the number of completed purchases). Heights are presentation only and
 * never imply traffic (docs/01 section 3).
 */
export interface CommercialStyle {
  id: CommercialTemplateId;
  /** Footprint occupied inside the plot (metres). Identical for every style so plots stay uniform. */
  footprint: { width: number; depth: number };
  roof: "flat" | "gable" | "setback" | "crown";
  /** Rooftop sign board (what the overview camera reads). */
  rooftopSign: { width: number; height: number };
  /** Storefront sign above the door (what walkers read). */
  storefrontSign: { width: number; height: number };
  /** Awning / canopy over the entrance. */
  canopy: boolean;
}

const FOOTPRINT = { width: 10, depth: 12 } as const;
const ROOFTOP_SIGN = { width: 9.6, height: 7.2 } as const;
const STOREFRONT_SIGN = { width: 8, height: 2.2 } as const;

export const COMMERCIAL_TEMPLATES: Record<CommercialTemplateId, CommercialStyle> = {
  small_shop: { id: "small_shop", footprint: FOOTPRINT, roof: "gable", rooftopSign: ROOFTOP_SIGN, storefrontSign: STOREFRONT_SIGN, canopy: true },
  corner_shop: { id: "corner_shop", footprint: FOOTPRINT, roof: "flat", rooftopSign: ROOFTOP_SIGN, storefrontSign: STOREFRONT_SIGN, canopy: true },
  office_block: { id: "office_block", footprint: FOOTPRINT, roof: "flat", rooftopSign: ROOFTOP_SIGN, storefrontSign: STOREFRONT_SIGN, canopy: false },
  modern_midrise: { id: "modern_midrise", footprint: FOOTPRINT, roof: "setback", rooftopSign: ROOFTOP_SIGN, storefrontSign: STOREFRONT_SIGN, canopy: true },
  colorful_tower: { id: "colorful_tower", footprint: FOOTPRINT, roof: "flat", rooftopSign: ROOFTOP_SIGN, storefrontSign: STOREFRONT_SIGN, canopy: false },
  landmark_tower: { id: "landmark_tower", footprint: FOOTPRINT, roof: "crown", rooftopSign: ROOFTOP_SIGN, storefrontSign: STOREFRONT_SIGN, canopy: true },
};

/** Storefront sign centre height (metres above the sidewalk), the same for every stage. */
export const STOREFRONT_SIGN_Y = 3.7;

export interface CommercialHeightStage {
  stage: 0 | 1 | 2 | 3 | 4 | 5;
  heightMetres: number;
  floors: number;
}

/** Stage 0 = every building today; each completed purchase adds a stage up to a 40 m skyscraper. */
export const COMMERCIAL_HEIGHT_STAGES: readonly CommercialHeightStage[] = [
  { stage: 0, heightMetres: 10, floors: 3 },
  { stage: 1, heightMetres: 14, floors: 4 },
  { stage: 2, heightMetres: 19, floors: 6 },
  { stage: 3, heightMetres: 25, floors: 8 },
  { stage: 4, heightMetres: 32, floors: 10 },
  { stage: 5, heightMetres: 40, floors: 13 },
];
export const MAX_COMMERCIAL_HEIGHT_STAGE = 5;

/** Height stage for a building that has been purchased `acquisitionCount` times (seeded demo ownership = 0). */
export function commercialHeightStage(acquisitionCount: number): number {
  if (!Number.isFinite(acquisitionCount) || acquisitionCount <= 0) return 0;
  return Math.min(MAX_COMMERCIAL_HEIGHT_STAGE, Math.floor(acquisitionCount));
}

export function commercialStageInfo(stage: number): CommercialHeightStage {
  const s = Math.max(0, Math.min(MAX_COMMERCIAL_HEIGHT_STAGE, Math.floor(stage)));
  return COMMERCIAL_HEIGHT_STAGES[s]!;
}

/** Civic building shell (Townsy Real Estate office). */
export const CIVIC_OFFICE = {
  footprint: { width: 12, depth: 10 },
  heightMetres: 9,
  rooftopSign: { width: 10, height: 3.2 },
  storefrontSign: { width: 9, height: 2.2 },
} as const;

export interface HomeStageTemplate {
  id: HomeStageId;
  rank: number;
  name: string;
  footprint: { width: number; depth: number };
  heightMetres: number;
  roof: "none" | "gable" | "hip" | "flat" | "turrets";
  floors: number;
}

export const HOME_STAGE_TEMPLATES: Record<HomeStageId, HomeStageTemplate> = {
  tent: { id: "tent", rank: 0, name: "Tent", footprint: { width: 3, depth: 3 }, heightMetres: 2.2, roof: "none", floors: 1 },
  log_cabin: { id: "log_cabin", rank: 1, name: "Log cabin", footprint: { width: 6, depth: 5 }, heightMetres: 4, roof: "gable", floors: 1 },
  trailer_home: { id: "trailer_home", rank: 2, name: "Trailer home", footprint: { width: 8, depth: 3.4 }, heightMetres: 3.4, roof: "flat", floors: 1 },
  small_house: { id: "small_house", rank: 3, name: "Small house", footprint: { width: 8, depth: 7 }, heightMetres: 6, roof: "gable", floors: 1 },
  medium_house: { id: "medium_house", rank: 4, name: "Medium house", footprint: { width: 10, depth: 8 }, heightMetres: 7.5, roof: "hip", floors: 2 },
  mansion: { id: "mansion", rank: 5, name: "Mansion", footprint: { width: 12, depth: 9 }, heightMetres: 9, roof: "hip", floors: 3 },
  castle: { id: "castle", rank: 6, name: "Castle", footprint: { width: 13, depth: 10 }, heightMetres: 12, roof: "turrets", floors: 3 },
};

/** The largest footprint any home stage occupies; an OWNED home's collider uses this so upgrades never trap visitors. */
export const HOME_MAX_FOOTPRINT = { width: 13, depth: 10 } as const;
